/**
 * SourceCards - Collapsible Reference Material Panel
 *
 * Shows read-only reference material cards below the draft editor.
 * These are non-draft-chapter ContentItems in the buffer that serve
 * as source material for the draft.
 *
 * @module @humanizer/studio/components/workspace/SourceCards
 */

import React from 'react';
import type { ContentItem } from '../../contexts/BufferSyncContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SourceCardsProps {
  items: ContentItem[];
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SourceCards({ items, className = '' }: SourceCardsProps): React.ReactElement {
  if (items.length === 0) {
    return (
      <div className={`source-cards source-cards--empty ${className}`}>
        <p className="source-cards__empty-text">No source material loaded.</p>
      </div>
    );
  }

  return (
    <div className={`source-cards ${className}`}>
      <div className="source-cards__header">
        <span className="source-cards__title">Source Material</span>
        <span className="source-cards__count">{items.length}</span>
      </div>
      <div className="source-cards__list">
        {items.map((item) => {
          const title =
            item.metadata.source?.threadTitle ??
            item.metadata.source?.path?.[0] ??
            item.type;
          const preview = item.text.slice(0, 150);
          const role = item.metadata.authorRole;

          return (
            <div key={item.id} className="source-cards__card">
              <div className="source-cards__card-header">
                <span className="source-cards__card-title">{title}</span>
                {role && (
                  <span className="source-cards__card-role">{role}</span>
                )}
              </div>
              <p className="source-cards__card-preview">
                {preview}{item.text.length > 150 ? '...' : ''}
              </p>
              {item.metadata.wordCount ? (
                <span className="source-cards__card-meta">
                  {item.metadata.wordCount} words
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SourceCards;
